import { IPagedResult } from '../models/HttpPagedResult';
import { IResult } from '../models/HttpResult';
import { MedicalServiceCategory, NewMedicalServiceCategory } from '../models/Entities/MedicalServices/MedicalServiceHierarchy';

// Functions
import * as Functions from '../functions/common';

// Error Tracer
import * as Sentry from '@sentry/react';

/*
 * Categorías de servicios médicos (MedicalInsuranceAgreements.API). El controller cuelga de
 * /medicalservices/categories para pasar por la ruta /medicalServices/{everything} del gateway
 * sin agregar rutas nuevas. Si el API desplegado todavía es anterior, la llamada da 404 y quien
 * la use degrada sin romper la pantalla.
 */
const BaseURL = process.env.REACT_APP_BILLER_API_GATEWAY + 'medicalServices/categories/';

const headers = (token: string) => ({
  Accept: 'application/json',
  'Content-Type': 'application/json',
  Authorization: 'Bearer ' + token
});

const handle = async <T,>(request: Promise<Response>): Promise<T> => {
  try {
    return await request
      .then((response) => {
        if (!response.ok) throw response;
        return response.json() as Promise<T>;
      })
      .catch((error: any) => {
        if (error.text)
          return error.text().then((body: any) => {
            throw Functions.HttpErrorHandler(body, error);
          });
        throw Functions.HttpErrorHandler(null, error);
      });
  } catch (error: any) {
    Sentry.captureException(error);
    throw error;
  }
};

export const GetMedicalServiceCategories = async (
  pageNumber: number,
  pageSize: number,
  searchText: string | undefined,
  token: string
): Promise<IPagedResult<MedicalServiceCategory[]>> => {
  const params = new URLSearchParams({ pageNumber: String(pageNumber), pageSize: String(pageSize) });
  if (searchText) params.set('searchText', searchText);
  return handle<IPagedResult<MedicalServiceCategory[]>>(fetch(BaseURL + '?' + params.toString(), { headers: headers(token) }));
};

export const AddMedicalServiceCategory = async (category: NewMedicalServiceCategory, token: string): Promise<number> =>
  (await handle<IResult<number>>(fetch(BaseURL, { method: 'POST', headers: headers(token), body: JSON.stringify(category) }))).data;

export const UpdateMedicalServiceCategory = async (category: MedicalServiceCategory, token: string): Promise<number> =>
  (await handle<IResult<number>>(fetch(BaseURL, { method: 'PUT', headers: headers(token), body: JSON.stringify(category) }))).data;

export const DeleteMedicalServiceCategory = async (categoryId: number, token: string): Promise<void> => {
  await handle<unknown>(fetch(BaseURL + categoryId, { method: 'DELETE', headers: headers(token) }));
};

const ALL_PAGE_SIZE = 100;
const ALL_MAX_PAGES = 10;

/** Todas las categorías del tenant, paginando de a 100 y ordenadas por nombre. */
export const GetAllMedicalServiceCategories = async (token: string): Promise<MedicalServiceCategory[]> => {
  const all: MedicalServiceCategory[] = [];
  for (let page = 1; page <= ALL_MAX_PAGES; page += 1) {
    const result = await GetMedicalServiceCategories(page, ALL_PAGE_SIZE, undefined, token);
    all.push(...(result.data ?? []));
    if (page >= (result.totalPages ?? 1)) break;
  }
  return all.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' }));
};
